/* TenantMobileNav — shared mobile-only nav drawer for every per-tenant
   template (`/s/:slug/...`).

   Why it exists: every template's masthead `<nav>` is hidden below the
   `md` breakpoint (768px) — perfect for a wide canvas, useless on a
   phone. On a phone the entire nav (Home / Portfolio / Services /
   Contact) vanished, so studios' clients had no way to leave the
   landing hero. This component is the single mobile escape hatch.

   It's deliberately design-neutral: only structure + spacing + the
   tenant CSS variables (`--tenant-bg`, `--tenant-fg`, `--tenant-accent`,
   `--tenant-serif`, `--tenant-sans`) so it visually adopts whatever
   template it's mounted in — editorial's warm dark with gold accents,
   minimal's clean stone, brutalist's raw mono, etc. — without a
   per-template fork.

   Mount it inside the template's `<header>` so the hamburger sits beside
   the wordmark; the full-screen panel handles its own z-index. */

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { tenantHref } from "@/lib/tenant";

const NAV_ITEMS = [
  { to: "/", label: "Home", end: true },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/services", label: "Services" },
  { to: "/contact", label: "Contact" },
];

/**
 * @param {object} props
 * @param {"light" | "dark"} [props.tone] — controls the trigger icon contrast
 *   when the template's --tenant-fg isn't readable on the masthead bg.
 *   Defaults to "dark" (light-on-dark masthead — most editorial templates).
 * @param {string} [props.ctaLabel] — bottom CTA label (default "Book a session").
 * @param {string} [props.ctaTo] — bottom CTA target path (default "/booking").
 * @param {string} [props.studioName] — optional studio name rendered as the
 *   centered eyebrow inside the open panel (echo of the masthead wordmark).
 */
export default function TenantMobileNav({
  tone = "dark",
  ctaLabel = "Book a session",
  ctaTo = "/booking",
  studioName,
}) {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Close the drawer on route change — NavLink clicks inside the panel
  // route via React Router, so the URL updates and we react to it here
  // instead of binding an onClick to every link.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Lock background scroll while the drawer is open so the panel reads
  // as a true overlay (no jitter from the page scrolling underneath).
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Close on Escape — cheap a11y win.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Copy the live tenant CSS variables onto the portalled panel so it
  // matches the studio's palette. Portal mounts at document.body, which
  // breaks normal CSS-var inheritance from the template's root div, so we
  // resolve the vars where they ARE defined (the trigger button's parent
  // chain — same tree as the rest of the template) and pin them as inline
  // styles on the panel. Snapshotting on open means we only pay the
  // getComputedStyle cost once per open, not on every render.
  const [tenantVars, setTenantVars] = useState({
    bg: "#0A0908",
    fg: "#F5F2EC",
    accent: "#C9A961",
    serif: "",
    sans: "",
  });
  useEffect(() => {
    if (!open) return;
    // The trigger button is rendered inside the template's tree, so its
    // computed styles include the inherited tenant CSS vars.
    const trigger = document.querySelector('[data-testid="tenant-mobile-nav-trigger"]');
    if (!trigger) return;
    const cs = getComputedStyle(trigger);
    const read = (name, fallback) => {
      const v = (cs.getPropertyValue(name) || "").trim();
      return v || fallback;
    };
    setTenantVars({
      bg: read("--tenant-bg", "#0A0908"),
      fg: read("--tenant-fg", "#F5F2EC"),
      accent: read("--tenant-accent", "#C9A961"),
      serif: read("--tenant-serif", ""),
      sans: read("--tenant-sans", ""),
    });
  }, [open]);

  const iconColor =
    tone === "light"
      ? "var(--tenant-fg, #0A0908)"
      : "var(--tenant-fg, #F5F2EC)";

  return (
    <>
      {/* Trigger — masthead-mounted, mobile-only. Inherits the template
          foreground via CSS var so it reads correctly on both dark
          (editorial / spotlight) and light (minimal / journal / botanical)
          mastheads. */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="md:hidden inline-flex items-center justify-center w-10 h-10 -mr-2 transition-opacity hover:opacity-70"
        style={{ color: iconColor }}
        aria-label="Open menu"
        aria-expanded={open}
        data-testid="tenant-mobile-nav-trigger"
      >
        <Menu className="w-5 h-5" strokeWidth={1.5} />
      </button>

      {/* Full-screen panel. Mounted only when `open` so the closed state
          adds zero DOM overhead.

          Rendered via React Portal at document.body so the panel is a
          sibling of <body>, not a descendant of the tenant template's
          markup. Why this matters on iOS Safari: if ANY ancestor of a
          `position: fixed` element has `transform`, `filter`,
          `backdrop-filter`, `perspective`, `will-change`, or `contain:
          paint`, the W3C spec turns that ancestor into the containing
          block for the fixed child — meaning the panel suddenly takes
          its dimensions from the ancestor instead of the viewport.
          Templates often grow these properties over time (a fancy hover
          on the masthead, a parallax wrapper, a Tailwind `transform-gpu`
          left over from a transition class), and a single one breaks
          the drawer into "stuck-at-the-top, page-content-visible-below"
          mode — the exact symptom Skenzy Studio's screenshot showed.
          Portalling sidesteps the whole class of bugs.

          We also removed the `animate-in fade-in` enter animation that
          briefly held opacity at 0 → 1: when iOS Safari is under
          memory pressure it sometimes stalls CSS animations partway
          through, leaving the drawer permanently at low opacity. The
          panel just appears solid now. */}
      {open && createPortal(
        <div
          className="fixed inset-0 z-[80] md:hidden flex flex-col"
          style={{
            // Solid resolved bg (read from the trigger's computed style
            // when the panel opens — see the `tenantVars` snapshot above).
            // Portalled element can't inherit CSS vars from the template
            // root, so we copy them in explicitly. The `#0A0908` literal
            // here is just for defensive belt-and-suspenders: if the
            // snapshot somehow returns an empty string, we still render
            // an opaque panel instead of letting the page bleed through.
            background: tenantVars.bg || "#0A0908",
            color: tenantVars.fg || "#F5F2EC",
            fontFamily: tenantVars.sans || "inherit",
            opacity: 1,
          }}
          role="dialog"
          aria-modal="true"
          aria-label="Site navigation"
          data-testid="tenant-mobile-nav-panel"
        >
          {/* Panel header — mirrors the masthead height (h-20) so the
              close button lines up where the hamburger was. Wordmark
              echoed as a quiet eyebrow if the host passes studioName. */}
          <div className="h-20 px-6 flex items-center justify-between">
            <span
              className="text-[11px] uppercase tracking-[0.32em] truncate"
              style={{ color: tenantVars.fg || "#F5F2EC", opacity: 0.85 }}
              data-testid="tenant-mobile-nav-wordmark"
            >
              {studioName || ""}
            </span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="inline-flex items-center justify-center w-10 h-10 -mr-2 transition-opacity hover:opacity-70"
              style={{ color: tenantVars.fg || "#F5F2EC" }}
              aria-label="Close menu"
              data-testid="tenant-mobile-nav-close"
            >
              <X className="w-5 h-5" strokeWidth={1.5} />
            </button>
          </div>

          {/* Center nav stack — large editorial-style serif italics so the
              menu feels intentional, not utilitarian. Active route reads
              at full opacity; inactive at 0.45 so the user can see where
              they are. */}
          <nav
            className="flex-1 flex flex-col items-center justify-center gap-7 px-6"
            aria-label="Mobile primary"
          >
            {NAV_ITEMS.map((it) => (
              <NavLink
                key={it.to}
                to={tenantHref(it.to)}
                end={it.end}
                data-testid={`tenant-mobile-nav-link-${it.label.toLowerCase()}`}
                className="text-3xl transition-opacity"
                style={({ isActive }) => ({
                  fontFamily: tenantVars.serif || "inherit",
                  fontStyle: "italic",
                  fontWeight: 300,
                  letterSpacing: "0.005em",
                  opacity: isActive ? 1 : 0.45,
                  color: tenantVars.fg || "#F5F2EC",
                })}
              >
                {it.label}
              </NavLink>
            ))}
          </nav>

          {/* Bottom CTA — high-contrast pill matching the template's
              accent (or foreground if no accent is set). Gives mobile
              visitors the same "Book a session" affordance the desktop
              masthead has. */}
          <div className="px-6 pb-10 pt-4">
            <Link
              to={tenantHref(ctaTo)}
              onClick={() => setOpen(false)}
              className="flex items-center justify-center gap-2 h-14 text-[11px] uppercase tracking-[0.32em] font-medium transition-opacity hover:opacity-90"
              style={{
                background: tenantVars.accent || "#C9A961",
                color: tenantVars.bg || "#0A0908",
              }}
              data-testid="tenant-mobile-nav-cta"
            >
              {ctaLabel} →
            </Link>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
